import { useSelector } from 'react-redux';
import { lastElement, sum } from 'utils/arrays';
import { getQuantity } from 'utils/categories';
import { addPlusMinus5PercentageRangeToNumber, addThousandSeparator } from 'utils/numbers';
import CategoryCard from './CategoryCard';
import ForecastChart from './ForecastChart';
import styles from './scorecard.module.scss';
import { useTranslation } from 'react-i18next';
import { getFormatSeparatorsOptions } from 'config/marketConfig';

const TOP_CATEGORIES_COUNT = 3;

export default function Scorecard() {
  const { t } = useTranslation();
  const na = useSelector((state) => state.na);
  const currentWorkforce = na.growth[0];
  const projectedWorkforce = na.growth[na.growth.length - 1];
  const naSpaceSelected = na.spaces.find((naSpace) => naSpace.id === na.workplacestyleSelected);
  const startingYear = new Date(na.startingYear).getFullYear();
  const leaseExpYear = new Date(na.leaseExpirationYear).getFullYear();
  const totalSeats = lastElement(na.growth.map((growth_row) => growth_row.seats));
  const topCategories = naSpaceSelected.spaceTypes
    .concat(naSpaceSelected.customSpaces)
    .map((spaceType) => {
      return {
        id: spaceType.id,
        name: spaceType.name,
        quantity: spaceType.spaces
          ? getQuantity(spaceType, getFormatSeparatorsOptions())
          : spaceType.quantity,
        value: spaceType.spaces
          ? sum(spaceType.spaces, (space) => space.quantity * space.size.width * space.size.height)
          : spaceType.quantity * spaceType.width * spaceType.height,
      };
    })
    .sort((a, b) => b.value - a.value)
    .slice(0, TOP_CATEGORIES_COUNT);

  return (
    <div className={styles.container}>
      <div className={styles.content_left}>
        <div className={styles.assessment__info_card}>
          <div className={styles.assessment__card_title_container}>
            <h3 className={styles.assessment__card_title}>{t('step6.yourSituation')}</h3>
          </div>
          <div className={styles.assessment__card_content_container}>
            <div
              data-testid="ScorecardLeaseExpirationYear"
              className={styles.assessment__card_content}>
              <span className={styles.assessment__card_content_title}>
                {t('step6.leaseExpDate')}
              </span>
              <span className={styles.assessment__card_content_value}>
                {na.leaseExpirationYear ? leaseExpYear : '-'}
              </span>
            </div>
            <div className={styles.divider}></div>
            <div data-testid="ScorecardStartingYear" className={styles.assessment__card_content}>
              <span className={styles.assessment__card_content_title}>
                {t('step6.startingYear')}
              </span>
              <span className={styles.assessment__card_content_value}>{startingYear}</span>
            </div>
            <div data-testid="ScorecardNextLeaseTerm" className={styles.assessment__card_content}>
              <span className={styles.assessment__card_content_title}>
                {t('step6.nextLeaseTerm')}
              </span>
              <span className={styles.assessment__card_content_value}>
                {na.leaseTermInYears} {t('step6.years')}
              </span>
            </div>
          </div>
          <div className={styles.bottom_line}></div>
          <div className={styles.assessment__card_title_container}>
            <h3 className={styles.assessment__card_title}>{t('step6.forecastHeadcount')}</h3>
          </div>
          <div className={styles.assessment__card_content_container}>
            <div data-testid="ScorecardHeadCount" className={styles.assessment__card_content}>
              <span className={styles.assessment__card_content_title}>
                {t('step6.currentHeadcount')}
              </span>
              <span className={styles.assessment__card_content_value}>
                {addThousandSeparator(
                  currentWorkforce.totalHeadcount,
                  getFormatSeparatorsOptions(),
                )}
              </span>
            </div>
            <div className={styles.divider}></div>
            <div
              data-testid="ScorecardProjectedHeadCount"
              className={styles.assessment__card_content}>
              <span className={styles.assessment__card_content_title}>
                {t('step6.projectHeadcount')}
              </span>
              <span className={styles.assessment__card_content_value}>
                {addThousandSeparator(
                  projectedWorkforce.totalHeadcount,
                  getFormatSeparatorsOptions(),
                )}
              </span>
            </div>
          </div>
          <ForecastChart
            currentWorkforce={currentWorkforce}
            projectedWorkforce={projectedWorkforce}
          />
        </div>
      </div>
      <div className={styles.content_right}>
        <div
          data-testid="ScorecardRentableTotal"
          className={`${styles.assessment__office_info_card} ${styles.assessment__office_info_card_rsf}`}>
          <span className={styles.assessment__office_info_value}>
            {addPlusMinus5PercentageRangeToNumber(
              naSpaceSelected.rentableSquareFeet,
              getFormatSeparatorsOptions(),
            )}
          </span>
          <h3 className={styles.assessment__office_info_title}>{t('step6.rentableTotal')}</h3>
        </div>
        <div className={styles.assessment__info_container}>
          <div
            data-testid="ScorecardRentablePerHead"
            className={`${styles.assessment__office_info_card} ${styles.assessment__office_info_card_per_employee}`}>
            <span className={styles.assessment__office_info_value}>
              {addThousandSeparator(
                naSpaceSelected.rentableSquareFeetPerHead,
                getFormatSeparatorsOptions(),
              )}
            </span>
            <h3 className={styles.assessment__office_info_title}>
              {t('step6.inOfficeAndHybridTotal')}
            </h3>
          </div>
          <div
            data-testid="ScorecardTotalSeats"
            className={`${styles.assessment__office_info_card} ${styles.assessment__office_info_card_seats}`}>
            <span className={styles.assessment__office_info_value}>
              {addThousandSeparator(totalSeats, getFormatSeparatorsOptions())}
            </span>
            <h3 className={styles.assessment__office_info_title}>
              {t('step6.inOfficeAndHybridEmployees')}
            </h3>
          </div>
        </div>
        <div className={styles.categories_wrapper}>
          <div
            data-testid="ScorecardTopCategories"
            className={styles.assessment__card_title_container}>
            <h3 className={styles.assessment__card_title}>
              {t('step6.topCategories', { count: TOP_CATEGORIES_COUNT })}
            </h3>
          </div>
          <div className={styles.categories_container}>
            {topCategories.map((item) => (
              <CategoryCard
                key={item.id}
                value={addThousandSeparator(item.value, getFormatSeparatorsOptions())}
                title={`${item.quantity} ${t(item.name)}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
